import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Alert,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const statusOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "active", label: "Active" },
  { value: "locked", label: "Locked" },
  { value: "completed", label: "Completed" },
];

const MatchdayForm = ({
  initialData = null,
  tournamentId,
  onSubmit,
  onCancel,
  existingMatchdayNumbers = [],
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    matchdayNumber: "",
    name: "",
    expectedMatchCount: 4,
    status: "upcoming",
    budgetInitial: "",
    startsAt: null,
    endsAt: null,
    locksAt: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        matchdayNumber: initialData.matchdayNumber || "",
        name: initialData.name || "",
        expectedMatchCount: initialData.expectedMatchCount || 4,
        status: initialData.status || "upcoming",
        budgetInitial: initialData.budgetInitial ?? "",
        startsAt: initialData.startsAt ? new Date(initialData.startsAt) : null,
        endsAt: initialData.endsAt ? new Date(initialData.endsAt) : null,
        locksAt: initialData.locksAt ? new Date(initialData.locksAt) : null,
      });
    }
  }, [initialData]);

  const validate = () => {
    const newErrors = {};

    if (!formData.matchdayNumber || formData.matchdayNumber < 1) {
      newErrors.matchdayNumber =
        "Matchday number is required and must be at least 1";
    } else if (
      !isEdit &&
      existingMatchdayNumbers.includes(parseInt(formData.matchdayNumber))
    ) {
      newErrors.matchdayNumber =
        "Matchday number already exists for this tournament";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.expectedMatchCount || formData.expectedMatchCount < 1) {
      newErrors.expectedMatchCount = "Expected match count must be at least 1";
    }

    if (
      formData.startsAt &&
      formData.endsAt &&
      formData.startsAt > formData.endsAt
    ) {
      newErrors.endsAt = "End date must be after start date";
    }

    if (
      formData.locksAt &&
      formData.startsAt &&
      formData.locksAt > formData.startsAt
    ) {
      newErrors.locksAt = "Lock time should be before or at start time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        ...formData,
        matchdayNumber: parseInt(formData.matchdayNumber),
        expectedMatchCount: parseInt(formData.expectedMatchCount),
        budgetInitial: formData.budgetInitial
          ? parseInt(formData.budgetInitial)
          : undefined,
        tournamentId,
      });
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Matchday Number */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Matchday Number *"
              type="number"
              value={formData.matchdayNumber}
              onChange={handleChange("matchdayNumber")}
              error={!!errors.matchdayNumber}
              helperText={
                errors.matchdayNumber || "Unique number for this matchday"
              }
              disabled={isEdit}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Expected Match Count */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Expected Match Count *"
              type="number"
              value={formData.expectedMatchCount}
              onChange={handleChange("expectedMatchCount")}
              error={!!errors.expectedMatchCount}
              helperText={
                errors.expectedMatchCount ||
                "How many matches will be in this matchday"
              }
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Matchday Name *"
              value={formData.name}
              onChange={handleChange("name")}
              error={!!errors.name}
              helperText={
                errors.name || 'e.g., "Matchday 1 - Group Stage Openers"'
              }
              placeholder="Enter matchday name"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>Status *</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange("status")}
                label="Status *"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Initial Budget */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Initial Budget"
              type="number"
              value={formData.budgetInitial}
              onChange={handleChange("budgetInitial")}
              helperText="Team budget for this matchday (e.g. 50000000)"
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Dates */}
          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Starts At"
              value={formData.startsAt}
              onChange={handleDateChange("startsAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.startsAt}
                  helperText={errors.startsAt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Ends At"
              value={formData.endsAt}
              onChange={handleDateChange("endsAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.endsAt}
                  helperText={errors.endsAt}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePicker
              label="Locks At (Transfer Deadline)"
              value={formData.locksAt}
              onChange={handleDateChange("locksAt")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.locksAt}
                  helperText={errors.locksAt}
                />
              )}
            />
          </Grid>

          {/* Info Alert */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Status Guide:</strong>
                <br />• <strong>Upcoming:</strong> Matchday is scheduled but not
                yet active
                <br />• <strong>Active:</strong> Users can make transfers and
                changes
                <br />• <strong>Locked:</strong> Transfers are closed, matches
                are live or about to start
                <br />• <strong>Completed:</strong> All matches finished, points
                calculated
              </Typography>
            </Alert>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
              >
                {isEdit ? "Save Changes" : "Create Matchday"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default MatchdayForm;
